export interface TopicSection {
  heading: string;
  paragraphs: string[];
}

export interface TopicContent {
  intro: string;
  quote: string;
  sections: TopicSection[];
}

export const topicContents: Record<string, TopicContent> = {
  'changing-cast-1998': {
    intro:
      '在档案室里，这场风波最早只是三页场记单上的涂改痕迹。第一版通告单印着一个名字，第二版换成了另一个人，中间只隔了三天。',
    quote: '官方口径只有一句“档期不合”，但材料显示，变动发生在最后一次剧本围读之后。',
    sections: [
      {
        heading: '通告单上的两个名字',
        paragraphs: [
          '1998 年 6 月的开机通告单是第一版，主演一栏印着 A 组演员的名字；同期流出的第二版通告单上，这个名字被划掉，旁边手写了另一个名字。两份通告单的印刷字号、页脚编号都能对上，说明它们出自同一次排印，只是改版时间不同。',
          '把两份通告单按页脚编号排开，能看到中间还缺了一页。缺失的这页恰好是改动发生的那一天，这也是档案里最明显的一处空白。',
        ],
      },
      {
        heading: '脚本围读记录说了什么',
        paragraphs: [
          '围读记录显示，变动前最后一次围读持续了将近五个小时，比原计划多出两小时。记录末尾只写了一行“剧本调整，另行通知”，没有说明调整的具体内容。',
          '值得注意的是，同期一位编剧的名字也从片头字幕的后备名单里消失了。这条线索后来被整理进了同一时间线，但两者之间是否存在直接关联，目前材料还不足以判断。',
        ],
      },
      {
        heading: '仍然存疑的部分',
        paragraphs: [
          '换角的原因，公开材料始终只有“档期不合”一句。档案室比对同期两位演员的行程后发现，其一的档期确实与拍摄期重叠，但另一位的行程也并非完全空闲。',
          '因此我们保留两种可能，不做结论：一是档期确实冲突，二是档期只是一个体面的说法。要判断哪一种更接近事实，还需要更多当年的排期凭证。',
        ],
      },
    ],
  },
  'studio-thirty-years': {
    intro:
      '把一家老牌工作室三十年的项目排期表按年份拼起来，会得到一条比报道更平滑的曲线：它几乎没有突然的崩塌，只有持续了十几年的缓慢收缩。',
    quote: '真正值得记录的往往不是某个高光时刻，而是排期表上每年少掉的那两三行。',
    sections: [
      {
        heading: '从三个人的小作坊开始',
        paragraphs: [
          '最早的三年，排期表上手写记录的项目只有个位数，制作周期短，署名结构也简单。这一阶段的材料以手写为主，纸张规格不统一，说明当时还没有固定的行政流程。',
          '到了第二年，排期表开始出现统一印制的表格，项目编号也变成连续编号。这是一个机构开始规范化的明显信号。',
        ],
      },
      {
        heading: '年产十余部的那几年',
        paragraphs: [
          '进入高峰期后，排期表被拆成多个分表，每个分表对应一类项目。此时的项目编号已经出现跳号，说明有一部分立项最终没有进入制作。',
          '档案室统计发现，跳号的比例在巅峰期反而最高。也就是说，看上去最忙碌的那几年，也是废弃立项最多的一段时间。',
        ],
      },
      {
        heading: '收缩是怎样发生的',
        paragraphs: [
          '收缩并不戏剧化。排期表上先是合作方一栏的名字逐渐变少，然后是分表被合并，最后是项目编号重新开始编号。整个过程持续了大约十二年。',
          '如果把项目数量画成折线，它更像一条缓慢下滑的斜线，而不是断崖。这一点与后来流传的“一夜之间解散”的说法并不一致。',
        ],
      },
    ],
  },
  'three-deleted-minutes': {
    intro:
      '公映版本比送审版本短了三分钟。三分钟在成片里并不显眼，但把它们所在的镜头逐一取出后，会发现删减的位置相当集中。',
    quote: '被删掉的三分钟里，有两分钟属于同一段人物关系。',
    sections: [
      {
        heading: '先确定差异在哪里',
        paragraphs: [
          '档案室拿到的是剪辑日志与公映版本的时间码对照。以场次为基准逐条排查后，确认共有十一处不同，其中七处集中在第三幕的前半段。',
          '这七处的共同点是镜头都停留在同一组人物之间，且多为无台词的停顿。换句话说，删减针对的是关系，而不是情节。',
        ],
      },
      {
        heading: '时间码之外的信息',
        paragraphs: [
          '剪辑日志的页边有一行淡淡的手写批注，字迹与正文不同，内容只有四个字：“节奏再收”。这句批注出现在第三幕的第一次修改记录旁。',
          '由于批注没有署名，无法确认出自谁手。但它的位置恰好对应最终删减最集中的那一段，因此被列为重要但不充分的旁证。',
        ],
      },
      {
        heading: '结论与保留',
        paragraphs: [
          '可以确定的是：删减不是零散的微调，而是主要集中在同一段人物关系上。至于原因，材料只能提供“节奏调整”这一种解释。',
          '是否有其他考虑，目前没有可核对的凭证。我们倾向于把这一段作为开放问题保留下来。',
        ],
      },
    ],
  },
  'five-blank-years': {
    intro:
      '所谓五年空白期，是以正式发行为标准得出的印象。一旦把版权登记、合作署名与零星通告也算进来，时间轴上就会出现不少小点。',
    quote: '空白期并不真的空白，只是这段时期的记录不再以“作品”的形式出现。',
    sections: [
      {
        heading: '先定义“空白”',
        paragraphs: [
          '如果以正式发行或大型演出为口径，这五年确实只有一次公开露面。但档案室采用的是一套更宽松的口径：只要留下与本人相关的可核对记录，就算作一个时间点。',
          '按这个口径，五年里共找到十九个时间点，分布并不均匀，前两年密集，中间两年几乎为零。',
        ],
      },
      {
        heading: '两个密集期',
        paragraphs: [
          '第一年与第二年出现了较多版权登记记录，登记类型以词曲授权为主。这说明创作活动可能仍在继续，只是没有以个人作品的形式发布。',
          '第四年与第五年则集中出现合作署名，多为幕后参与。这两类记录性质不同，说明参与方式在这一时期发生过变化。',
        ],
      },
      {
        heading: '中间那两年',
        paragraphs: [
          '真正没有任何可核对记录的，是第三年前后的大约二十四个月。这一段才是严格意义上的空白。',
          '档案室没有为这段空白补充任何推测性描述，只标注了它的起止时间。承认空白，本身就是资料整理的一部分。',
        ],
      },
    ],
  },
  'letter-in-the-theatre': {
    intro:
      '一封从未寄出的信，落款只留了姓，收信人也只有一个称谓。它被夹在一叠旧节目单里，跟着库房一起搬了三次家。',
    quote: '信纸的边缘已经发脆，但落款日期清楚：与那部戏的末场演出只隔了一天。',
    sections: [
      {
        heading: '信件本身的线索',
        paragraphs: [
          '纸张是当年剧场常用的稿纸，页眉印着剧场名称与一行小字。信封缺失，因此没有邮戳，也无法确认收信地址。',
          '正文提到一场“昨天散场后的谈话”，并多次使用同一部戏里的角色名代替真实称呼。这种写法在当年的演职人员往来书信中比较常见。',
        ],
      },
      {
        heading: '与行程表的对照',
        paragraphs: [
          '档案室比对同期两位演员的行程表，发现落款日前后的三天，两人都登记在同一剧场。这是目前唯一能够把信与具体人物联系起来的材料。',
          '需要说明的是，同一时期在该剧场登记的演职人员共有十几位。仅凭行程重合，无法完成确证。',
        ],
      },
      {
        heading: '为什么仍然收录',
        paragraphs: [
          '尽管无法确证收信人，这封信仍然有它的价值：它记录了当时工作关系之外的一层情感与表达方式，是制度性材料里看不到的部分。',
          '因此档案室按“来源不明、内容真实”归入馆藏，并在条目下明确标注了存疑项。',
        ],
      },
    ],
  },
  'backstage-forty-eight-hours': {
    intro:
      '颁奖礼的后台有两套秩序：一套写在流程单上，一套藏在座位表与临时改动的口播稿里。四十八小时里，后者至少改过五次。',
    quote: '台上看起来一气呵成，台下其实在最后一分钟还在换顺序。',
    sections: [
      {
        heading: '从彩排到开场',
        paragraphs: [
          '彩排版的流程单与正式版的差异主要在颁奖环节。正式版把两个奖项的位置做了对调，并缩短了其中一段串场。',
          '座位表也同步调整过。被调换位置的三组人恰好都出现在对调后的奖项附近，说明流程与座次是配套修改的。',
        ],
      },
      {
        heading: '口播稿的五次改动',
        paragraphs: [
          '口播稿留有五个版本，每版都标注了修改时间。最后一次修改发生在开场前四十分钟，改动内容只有一句过渡语。',
          '这句话的措辞在前后版本中语气明显不同，从陈述改成了引导。改动的动机无法从材料中确认。',
        ],
      },
      {
        heading: '散场之后',
        paragraphs: [
          '流程单在散场后被收回，但后台的座位表被留了下来。它成为判断流程改动范围的关键材料。',
          '四十八小时里真正被记录的，其实是这些看不见的调整次数。台前的顺利完成，正是这些调整的结果。',
        ],
      },
    ],
  },
  'six-drafts-of-a-script': {
    intro:
      '同一部作品的六个改稿版本，被分别存放在三个不同的档案袋里。把它们的页码、批注与页眉格式对齐后，署名争议的焦点就变得具体了。',
    quote: '争议的核心不是“谁写了”，而是“哪一稿才算最终定稿”。',
    sections: [
      {
        heading: '六稿的基本差异',
        paragraphs: [
          '第一稿与第二稿的批注格式一致，使用的是同一种蓝色墨水；第三稿起改用铅笔，页边批注的措辞也变得更简短。',
          '第四稿的页眉第一次出现了另一方的名字缩写，第五稿该缩写被删除，第六稿又恢复。这三稿构成了争议最集中的部分。',
        ],
      },
      {
        heading: '笔迹与批注',
        paragraphs: [
          '档案室对批注笔迹做了横向比对，确认至少有三种不同笔迹参与了修改。其中一种笔迹集中出现在关键转折处。',
          '由于无法取得当事人的笔迹样本，这一比对只能说明“存在多人参与”，无法直接对应到具体的人。',
        ],
      },
      {
        heading: '可以确认的部分',
        paragraphs: [
          '能够确认的是：作品在定稿前经历了至少六个版本、三种笔迹、多轮结构性调整。这已经足以说明它是集体改稿的结果。',
          '至于署名如何分配，公开材料没有给出过程记录，因此档案室不对此下结论。',
        ],
      },
    ],
  },
  'the-unpublished-notes': {
    intro:
      '几十页从未刊发的采访笔记，被一位资深记者留在旧书柜里。笔记没有发表，却成了很多旧事仅存的旁证。',
    quote: '这些笔记的价值不在于说了什么，而在于它们记下了当时谁愿意说、谁不愿意说。',
    sections: [
      {
        heading: '笔记的构成',
        paragraphs: [
          '内容大体分为三类：可以直接引用的原话、仅作参考的背景说明，以及记者本人标注的疑问。三类内容使用了不同的书写位置加以区分。',
          '整理时我们只采用第一类与第三类，第二类因缺少可核对来源，未纳入条目。',
        ],
      },
      {
        heading: '去个人化处理',
        paragraphs: [
          '为保护与公共事务无关的私人信息，档案中心对涉及具体个人生活的段落做了整体删除，并保留了删除位置的标记。',
          '这种处理会损失一部分语境，但我们认为边界比完整更重要。',
        ],
      },
      {
        heading: '旁证的作用',
        paragraphs: [
          '这些笔记大多无法单独成立，但它们能在其他材料之间补上连接句。例如某次会议的时间，正是由笔记与一份行程表的交叉才得以确认。',
          '因此档案室把笔记定位为“旁证”，而非“结论”。',
        ],
      },
    ],
  },
  'rights-in-the-recording-room': {
    intro:
      '一张畅销专辑的制作名单，在不同版本里出现了三个署名。录音室进出日志恰好记录了其中一个名字消失的那几天。',
    quote: '署名变化总是发生在录音结束之后，而不是录制期间。',
    sections: [
      {
        heading: '三个版本的名单',
        paragraphs: [
          '最早的印刷版名单列了五位制作相关人员，后来的再版名单减少到四位，数字平台版本又增加了另一位。三份名单的排序方式也不相同。',
          '排序并非随机：排在前两位的名字在所有版本中位置都没变，变动的只有后面的名次。',
        ],
      },
      {
        heading: '录音室日志',
        paragraphs: [
          '进出日志显示，被删除的那位在专辑后期制作期间仍频繁出入录音室，甚至比名单上保留的部分人员来得更多。',
          '这一事实说明，署名变化的原因不太可能是“参与度不足”。但日志并不能说明真正的变动原因。',
        ],
      },
      {
        heading: '保留的疑问',
        paragraphs: [
          '可以确认的是：署名在录制结束后发生过改动，且与参与时长无关。改动由谁决定、依据什么，材料没有记录。',
          '档案室据此把这部分归入“存疑”，并附上三份名单的对照表供读者自查。',
        ],
      },
    ],
  },
  'one-line-that-changed-career': {
    intro:
      '直播里的那句话只有几秒，但它之后的五年里，节目单、主持排班与通告安排都在持续调整。余波比事故本身更长。',
    quote: '真正改变一个人的，往往不是那几秒钟，而是之后无数次的“暂不考虑”。',
    sections: [
      {
        heading: '事故当天的记录',
        paragraphs: [
          '当天的节目单与日播顺序都完整保留。发生口误的环节原本并未列入串词，属于临时接话。这一点在事后的复盘记录中有明确记载。',
          '同一份复盘记录还提到，现场并未中断录制，节目按原计划播完。这与后来流传的“直播中断”说法不一致。',
        ],
      },
      {
        heading: '排班的五年变化',
        paragraphs: [
          '把五年的主持排班表按季度排列，能看出一个清晰趋势：黄金时段的主持频次逐季下降，其他时段保持稳定。',
          '下降是渐进的，没有出现某一次突然消失。这更像是一种长期的调整，而不是单一的处罚。',
        ],
      },
      {
        heading: '余波的形状',
        paragraphs: [
          '如果把通告数量画成折线，它会先快速下滑，然后在一个较低的水平上趋于平稳。这条曲线的形状，比任何一句结论都更能说明问题。',
          '至于调整是否与那几秒直接相关，公开材料并未给出因果说明。我们只呈现时间上的先后关系。',
        ],
      },
    ],
  },
};